import { ModelFactory } from "../../ModelFactory";
import { Customer, customerFactory } from "../../common/models/Customer";
import { Contract, contractFactory } from "./Contract";
import { DeploymentStatus } from "../../common/models/DeploymentStatus";
import { parseItemPermissions, PermissionMap } from "../../common/models/PermissionMap";


export type MailType = "imap_mail_domain" | "transactional_mail_domain" | "ms_mail_domain"
export class MailDomain {
  constructor(
    public readonly id: string,
    public readonly displayName: string,
    public readonly notes: string | null,
    public readonly deploymentStatus: DeploymentStatus,
    public readonly deploymentDetails: MailDomainDeploymentDetails | TransactionalMailDomainDeploymentDetails | null,
    public readonly mailType: MailType,
    public readonly customer: Customer | null,
    public readonly contracts: Contract[],
    public readonly permissions: PermissionMap | null,
  ) {
  }
}

class MailDomainFactory extends ModelFactory<MailDomain> {
  protected fromJson(json: any): MailDomain {
    const mailType: MailType = json['type']
    return new MailDomain(
      json['id'],
      json['display_name'],
      json['notes'],
      json['deployment_status'],
      json['deployment_details'] ? this.parseDeploymentDetails(mailType, json['deployment_details']) : null,
      mailType,
      json['customer'] ? customerFactory.fromItemResponse(json['customer']) : null,
      json['contracts'] ? contractFactory.fromItemsResponse(json['contracts']) : [],
      parseItemPermissions(json['permissions']),
    );
  }

  private parseDeploymentDetails(mailType: MailType, json: object): MailDomainDeploymentDetails | TransactionalMailDomainDeploymentDetails {
    if (mailType === 'transactional_mail_domain') {
      return transactionalMailDomainDeploymentDetailsFactory.fromItemResponse(json)
    }
    return mailDomainDeploymentDetailsFactory.fromItemResponse(json)
  }
}
export const mailDomainFactory = new MailDomainFactory();

export class MailDomainDeploymentDetails {
  constructor(
    public readonly domain: string,
    public readonly mailboxes: MailBoxDeploymentDetails[],
    public readonly aliases: MailAliasDeploymentDetails[],
    public readonly forwards: MailForwardDeploymentDetails[],
  ) {
  }
}

class MailDomainDeploymentDetailsFactory extends ModelFactory<MailDomainDeploymentDetails> {
  protected fromJson(json: any): MailDomainDeploymentDetails {
    return new MailDomainDeploymentDetails(
      json['domain'],
      mailBoxDeploymentDetailsFactory.fromItemsResponse(json['mailboxes']),
      mailAliasDeploymentDetailsFactory.fromItemsResponse(json['aliases']),
      mailForwardDeploymentDetailsFactory.fromItemsResponse(json['forwards']),
    );
  }
}
const mailDomainDeploymentDetailsFactory = new MailDomainDeploymentDetailsFactory();

export class MailBoxDeploymentDetails {
  constructor(
    public readonly email: string,
    public readonly domain: string,
    public readonly quota: number,
    public readonly usage: number,
  ) {
  }
}

class MailBoxDeploymentDetailsFactory extends ModelFactory<MailBoxDeploymentDetails> {
  protected fromJson(json: any): MailBoxDeploymentDetails {
    return new MailBoxDeploymentDetails(
      json['email'],
      json['domain'],
      json['quota'],
      json['usage'],
    );
  }
}
const mailBoxDeploymentDetailsFactory = new MailBoxDeploymentDetailsFactory();


export class MailForwardDeploymentDetails {
  constructor(
    public readonly id: number,
    public readonly email: string,
    public readonly target: string,
  ) {
  }
}

class MailForwardDeploymentDetailsFactory extends ModelFactory<MailForwardDeploymentDetails> {
  protected fromJson(json: any): MailForwardDeploymentDetails {
    return new MailForwardDeploymentDetails(
      json['id'],
      json['email'],
      json['target'],
    );
  }
}
const mailForwardDeploymentDetailsFactory = new MailForwardDeploymentDetailsFactory();


export class MailAliasDeploymentDetails {
  constructor(
    public readonly id: number,
    public readonly email: string,
    public readonly alias: string,
  ) {
  }
}

class MailAliasDeploymentDetailsFactory extends ModelFactory<MailAliasDeploymentDetails> {
  protected fromJson(json: any): MailAliasDeploymentDetails {
    return new MailAliasDeploymentDetails(
      json['id'],
      json['email'],
      json['alias'],
    );
  }
}
const mailAliasDeploymentDetailsFactory = new MailAliasDeploymentDetailsFactory();


export class TransactionalMailDnsRecord {
  constructor(
    public readonly type: string,
    public readonly name: string,
    public readonly target: string,
  ) {
  }
}

class TransactionalMailDnsRecordFactory extends ModelFactory<TransactionalMailDnsRecord> {
  protected fromJson(json: any): TransactionalMailDnsRecord {
    return new TransactionalMailDnsRecord(
      json['type'],
      json['name'],
      json['target'],
    );
  }
}
const transactionalMailDnsRecordFactory = new TransactionalMailDnsRecordFactory();

export class TransactionalMailSmtpUser {
  constructor(
    public readonly id: number,
    public readonly emailAddress: string,
    public readonly deliveryMode: string,
  ) {
  }
}

class TransactionalMailSmtpUserFactory extends ModelFactory<TransactionalMailSmtpUser> {
  protected fromJson(json: any): TransactionalMailSmtpUser {
    return new TransactionalMailSmtpUser(
      json['id'],
      json['email_address'],
      json['delivery_mode'],
    );
  }
}
const transactionalMailSmtpUserFactory = new TransactionalMailSmtpUserFactory();

export class TransactionalMailDomainDeploymentDetails {
  constructor(
    public readonly domain: string,
    public readonly smtpUsers: TransactionalMailSmtpUser[],
    public readonly aliasDomains: string[],
    public readonly dnsRecords: TransactionalMailDnsRecord[],
  ) {
  }
}

class TransactionalMailDomainDeploymentDetailsFactory extends ModelFactory<TransactionalMailDomainDeploymentDetails> {
  protected fromJson(json: any): TransactionalMailDomainDeploymentDetails {
    return new TransactionalMailDomainDeploymentDetails(
      json['domain'],
      transactionalMailSmtpUserFactory.fromItemsResponse(json['smtp_users'] ?? []),
      json['alias_domains'] ?? [],
      transactionalMailDnsRecordFactory.fromItemsResponse(json['dns_records'] ?? []),
    );
  }
}
export const transactionalMailDomainDeploymentDetailsFactory = new TransactionalMailDomainDeploymentDetailsFactory();
